import dayjs from "dayjs";
import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { message, Modal } from "antd";

import useOrganization from "features/company/hooks/useOrganization";
import useOptions from "features/masterData/hooks/useOptions";
import { dataURLtoFile, generateRandomString } from "utils/helper";
import { ANNOUNCEMENT_TYPE } from "../schemas/newsSchema";
import * as services from "../services/newsApi";
import { setCurrentNews, setIsLoading } from "../slices/newsSlice";
import { useTranslation } from "react-i18next";

const FORMAT_DATE = "YYYY-MM-DD[T]HH:mm";

function toInitialValues(initialValues = {}) {
  const {
    attachment = [],
    department = [],
    employeeType = [],
    audienceGroup = [],
    ...rest
  } = initialValues;
  const modifiedValues = {
    ...rest,
    attachment: (attachment || []).map((a) => ({
      ...a,
      content: dataURLtoFile(`data:${a.ext};base64, ${a.content}`, a.fileName),
    })),
    pinAnnounce: initialValues.pinAnnounce === "Y",
    acknowledge: initialValues.acknowledge === "Y",
    startAnnounce:
      initialValues.startAnnounce && initialValues.startAnnounce !== null
        ? dayjs(initialValues.startAnnounce, FORMAT_DATE)
        : null,
    endAnnounce:
      initialValues.endAnnounce && initialValues.endAnnounce !== null
        ? dayjs(initialValues.endAnnounce, FORMAT_DATE)
        : null,
    announcementType:
      initialValues.announcementType || ANNOUNCEMENT_TYPE.BROADCAST_NOW,
    departmentId: department.departmentId
      ? [department.departmentId]
      : department.map((a) => a.departmentId),
    employeeTypeId: employeeType.id
      ? [employeeType.id]
      : employeeType.map((a) => a.id),
    audienceGroup: audienceGroup.id
      ? [audienceGroup.id]
      : audienceGroup.map((a) => a.id),
  };
  return modifiedValues;
}

function toParams(params) {
  const { startAnnounce, endAnnounce, ...rest } = params;
  console.log("cover", typeof params.cover == 'string');
  if ((typeof params.cover == 'string')) {
    params.cover = dataURLtoFile(
      "data:image/png;base64, " + params.cover,
      "example.png"
    );
  }

  const modifiedParams = {
    ...rest,
    cover: params.cover,
    pinAnnounce: params.pinAnnounce ? "Y" : "N",
    acknowledge: params.acknowledge ? "Y" : "N",
    startAnnounce: startAnnounce
      ? startAnnounce.format(FORMAT_DATE)
      : dayjs().format(FORMAT_DATE),
    endAnnounce: endAnnounce ? endAnnounce.format(FORMAT_DATE) : null,
  };
  return modifiedParams;
}

function useNews() {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const currentCompany = useSelector((state) => state.company.currentCompany);
  const isLoading = useSelector((state) => state.news.isLoading);
  const currentNews = useSelector((state) => state.news.currentNews);
  const { t, i18n } = useTranslation();

  const {
    organizationUnderOptions = [],
    departmentList = [],
    getDepartment,
  } = useOrganization();
  const {
    employeeOptions,
    employeeTypeOptions,
    rolePermissionOptions,
    getEmployeeTypeOptions,
    getRolePermission,
  } = useOptions();

  const initialValues = useMemo(() => {
    return toInitialValues(currentNews);
  }, [currentNews]);

  const getNews = useCallback(
    async (announcementNewsId) => {
      try {
        dispatch(setIsLoading(true));
        if (announcementNewsId) {
          const values = {
            announcementNewsId,
            requestId: generateRandomString(),
            companyId: currentCompany,
          };
          const response = await services.getAnnouncementNews(values);
          dispatch(setCurrentNews(response.data));
        }
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch]
  );

  const createNews = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));
        const result = await services.createAnnouncementNews(toParams(values));
        message.success(t("common.success"))
        navigate("/news_policy/news");
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [dispatch, navigate]
  );

  const editNews = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));

        const result = await services.updateAnnouncementNews(toParams(values));
        message.success(t("common.success"))
        navigate("/news_policy/news");
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [dispatch, navigate]
  );

  const onSubmit = useCallback(
    (values) => {
      console.log(values);
      const modifiedValues = {
        ...values,
        companyId: currentCompany,
      };
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (
          <>
            <p>{i18n.language == "en" ?
              `Are you sure confirm to ${values.announcementNewsId ? "edit" : "create"} News ?`
              : `คุณแน่ใจหรือไม่ที่จะยืนยันการ${values.announcementNewsId ? "แก้ไข" : "สร้าง"}ข่าวสาร ?`}
            </p>
          </>
        ),
        onOk() {
          if (values.announcementNewsId) {
            editNews(modifiedValues);
          } else {
            createNews(modifiedValues);
          }
        },
        okText: t("common.confirm"),
        cancelText: t("common.cancel"),
      });
    },
    [currentCompany, editNews, createNews]
  );

  const onBack = useCallback(() => {
    navigate("/news_policy/news");
  }, [navigate]);

  useEffect(() => {
    if (typeof currentCompany !== "object" && params.id) {
      getNews(params.id);
    }
  }, [currentCompany, params.id, getNews]);

  useEffect(() => {
    getDepartment();
  }, [getDepartment]);

  useEffect(() => {
    if (currentCompany != null) {
      getEmployeeTypeOptions({
        companyId: currentCompany,
        groupType: "EMPLOYEE_TYPE",
      });
    }
  }, [currentCompany, getEmployeeTypeOptions]);

  useEffect(() => {
    getRolePermission();
  }, [getRolePermission]);

  return {
    isLoading,
    initialValues,
    organizationUnderOptions,
    departmentList,
    employeeTypeOptions,
    employeeOptions,
    rolePermissionOptions,
    onSubmit,
    onBack,
  };
}

export default useNews;
